import {
  ActionButton,
  ActionButtonOption,
  ChevronDownIcon,
  IconSizeEnum,
  FontSizeEnum,
  SupportIcon,
  Tooltip,
  Typography,
} from '@acceso/design-system';
import { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import Listbox from '@/components/Listbox';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { TLogo } from '@/features/logos/types';
import { useSupportOptions } from '@/features/programs/hooks/useSupportOptions';
import { PresenceTypeEnum } from '@/types/presences';
import { EVisibility } from '@/types/types';
import { formatHMS } from '@/utils/time';

import { useTimeKeepingContext } from '../context/TimeKeepingContext';
import { useResetConfirmation } from '../hooks/use-reset-confirmation';
import { ItemLayoutType, TimeKeepingButtonStateEnum } from '../types';
import { buildCompositeKey } from '../utils/composite-key';
import { computeRecordsDuration } from '../utils/compute-records-duration';

import {
  AccordionEntry,
  DEFAULT_BUTTON_STATE,
  TimeKeepingButtonState,
  TimeKeepingAccordion,
} from './TimeKeepingAccordion';
import { TimeKeepingResetModal } from './TimeKeepingResetModal';

type TimeKeepingLogoCardProps = {
  item: ItemLayoutType;
  /** Resolved by the dashboard for every card; a card never asks for its own. */
  logo?: TLogo;
  z: number;
  onBringToTop: (compositeId: string) => void;
  onOpenVisibility: (
    compositeId: string,
    supportPresenceId: string,
    recordIndex: number,
    currentVisibility: EVisibility,
    el: HTMLElement
  ) => void;
};

export function TimeKeepingLogoCard({
  item,
  logo,
  z,
  onBringToTop,
  onOpenVisibility,
}: TimeKeepingLogoCardProps) {
  const { t } = useTranslation();
  const {
    sessionSupports,
    onRemoveItem,
    onResetRecords,
    onAddRecord,
    onUpdateRecord,
    onRemoveRecord,
    onItemSupportsChange,
    buildNewRecord,
  } = useTimeKeepingContext();
  const { playerVideoCurrentTime, playerVideoIsPlaying, getDateTimeCurrentPresence } =
    useBroadcastScheduleProgram();
  const { allSupports, supportOptions } = useSupportOptions();

  const compositeId = buildCompositeKey(PresenceTypeEnum.LOGOS, item.presence_id);
  const imageSrc = logo?.parameters?.main_logo_presigned_url ?? '';
  const displayName = logo?.name ?? item.presence_id;

  const [buttonStates, setButtonStates] = useState<Record<string, TimeKeepingButtonState>>({});
  const [openAccordions, setOpenAccordions] = useState<Map<string, AccordionEntry>>(new Map());
  const [openSelector, setOpenSelector] = useState(false);
  const [selectorCoords, setSelectorCoords] = useState<{ top: number; left: number } | null>(null);
  const liRefs = useRef<Map<string, HTMLLIElement>>(new Map());

  const allSupportKeys = item.supports.map(s => `${compositeId}:${s.presenceId}`);
  const allOpen = allSupportKeys.length > 0 && allSupportKeys.every(k => openAccordions.has(k));

  const filteredSupportOptions = supportOptions.filter(opt => sessionSupports.includes(opt.value));

  let totalSeconds = item.supports.reduce((acc, s) => acc + computeRecordsDuration(s.records), 0);
  for (const s of item.supports) {
    const bs = buttonStates[s.presenceId] ?? DEFAULT_BUTTON_STATE;
    if (bs.state === TimeKeepingButtonStateEnum.ACTIVE && bs.recordIndex !== undefined) {
      const activeRec = s.records[bs.recordIndex];
      if (activeRec && activeRec.datetimeEnd === '0') {
        const now = getDateTimeCurrentPresence(playerVideoCurrentTime) ?? new Date();
        const diff = now.getTime() - new Date(activeRec.datetimeStart).getTime();
        if (diff > 0) totalSeconds += diff / 1000;
      }
    }
  }
  const totalTime = formatHMS(totalSeconds);

  const toggleAccordion = useCallback(
    (key: string, el: HTMLElement, cId: string, supportPresenceId: string) => {
      setOpenAccordions(prev => {
        const next = new Map(prev);
        if (next.has(key)) {
          next.delete(key);
          return next;
        }
        const liElement = el.closest('li') as HTMLLIElement | null;
        if (!liElement) return prev;
        next.set(key, { key, compositeId: cId, supportPresenceId, liElement });
        return next;
      });
    },
    []
  );

  const toggleAllAccordions = useCallback(() => {
    setOpenAccordions(prev => {
      const next = new Map(prev);
      const allEntries = item.supports.map(s => ({
        key: `${compositeId}:${s.presenceId}`,
        supportPresenceId: s.presenceId,
      }));
      const allAreOpen = allEntries.length > 0 && allEntries.every(({ key }) => next.has(key));
      if (allAreOpen) {
        allEntries.forEach(({ key }) => next.delete(key));
      } else {
        allEntries.forEach(({ key, supportPresenceId }) => {
          if (!next.has(key)) {
            const liElement = liRefs.current.get(key);
            if (liElement) next.set(key, { key, compositeId, supportPresenceId, liElement });
          }
        });
      }
      return next;
    });
  }, [compositeId, item.supports]);

  const handlePlayPause = useCallback(
    (cId: string, supportPresenceId: string, currentRecordsLength: number) => {
      const current = buttonStates[supportPresenceId] ?? DEFAULT_BUTTON_STATE;

      if (current.state === TimeKeepingButtonStateEnum.INACTIVE) {
        onAddRecord(cId, supportPresenceId, buildNewRecord());
        setButtonStates(prev => ({
          ...prev,
          [supportPresenceId]: {
            state: TimeKeepingButtonStateEnum.ACTIVE,
            recordIndex: currentRecordsLength,
          },
        }));
        return;
      }

      if (current.recordIndex === undefined) {
        setButtonStates(prev => ({ ...prev, [supportPresenceId]: DEFAULT_BUTTON_STATE }));
        return;
      }

      const activeRecord = item.supports.find(s => s.presenceId === supportPresenceId)?.records[
        current.recordIndex
      ];

      if (!activeRecord) {
        setButtonStates(prev => ({ ...prev, [supportPresenceId]: DEFAULT_BUTTON_STATE }));
        return;
      }

      const endDate = getDateTimeCurrentPresence(playerVideoCurrentTime) ?? new Date();
      const startDate = new Date(activeRecord.datetimeStart);
      onUpdateRecord(cId, supportPresenceId, current.recordIndex, {
        datetimeEnd: endDate < startDate ? startDate : endDate,
      });
      setButtonStates(prev => ({ ...prev, [supportPresenceId]: DEFAULT_BUTTON_STATE }));
    },
    [
      buttonStates,
      item.supports,
      onAddRecord,
      onUpdateRecord,
      buildNewRecord,
      getDateTimeCurrentPresence,
      playerVideoCurrentTime,
    ]
  );

  const executeResetAll = useCallback(() => {
    onResetRecords(compositeId);
    setButtonStates(prev => {
      const next = { ...prev };
      item.supports.forEach(s => {
        next[s.presenceId] = DEFAULT_BUTTON_STATE;
      });
      return next;
    });
  }, [compositeId, item.supports, onResetRecords]);

  const executeResetSupport = useCallback(
    (cId: string, supportPresenceId: string) => {
      onResetRecords(cId, supportPresenceId);
      setButtonStates(prev => ({ ...prev, [supportPresenceId]: DEFAULT_BUTTON_STATE }));
    },
    [onResetRecords]
  );

  const { pendingReset, requestResetAll, requestResetSupport, cancelReset } =
    useResetConfirmation();

  const handleConfirmReset = useCallback(() => {
    if (!pendingReset) return;
    if (pendingReset.presenceId) {
      executeResetSupport(pendingReset.compositeId, pendingReset.presenceId);
    } else {
      executeResetAll();
    }
    cancelReset();
  }, [pendingReset, executeResetAll, executeResetSupport, cancelReset]);

  const handleToggleSupportSelector = useCallback(
    (el: HTMLElement) => {
      if (openSelector) {
        setOpenSelector(false);
        setSelectorCoords(null);
        return;
      }
      const rect = el.getBoundingClientRect();
      setSelectorCoords({ top: rect.bottom + 4, left: rect.left });
      setOpenSelector(true);
    },
    [openSelector]
  );

  const cardOptions: ActionButtonOption[] = [
    { label: t('Reiniciar tiempos'), action: () => requestResetAll(compositeId) },
    {
      label: allOpen ? t('Colapsar registros') : t('Desplegar registros'),
      action: toggleAllAccordions,
    },
    { label: t('Ocultar'), action: () => onRemoveItem(compositeId) },
  ];

  return (
    <div
      key={compositeId}
      data-id="card-widget"
      className="relative flex h-full flex-col overflow-hidden rounded-md border border-neutral-15 bg-white shadow-lg"
      style={{ zIndex: z }}
      onPointerDown={() => onBringToTop(compositeId)}
    >
      <div
        data-id="card-widget-header"
        className="card__widget__header cursor-move bg-gray-200 p-1"
      >
        <div data-id="card-widget-presence-resume" className="flex items-center justify-between">
          <img
            data-id="card-widget-presence-image"
            alt={displayName}
            src={imageSrc}
            className="h-10 w-10 object-contain"
          />
          <div className="z-500 flex items-center gap-2">
            <Typography fontSize={FontSizeEnum.Sm}>{totalTime}</Typography>
            <Tooltip help={t('Opciones')} grow>
              <ActionButton rounded={true} options={cardOptions} />
            </Tooltip>
          </div>
        </div>
        <div className="overflow-hidden text-ellipsis whitespace-nowrap pb-1 pt-2 text-[12px]">
          {displayName}
        </div>
        <div data-id="card-widget-supports-selector">
          <button
            type="button"
            className="w-full text-[11px] text-text-medium-10 hover:text-accent-active-primary-40"
            onPointerDown={e => e.stopPropagation()}
            onClick={e => handleToggleSupportSelector(e.currentTarget)}
          >
            <div className="flex items-center justify-between pr-1">
              <div className="flex items-center gap-1">
                <SupportIcon size={IconSizeEnum.SM} />
                {t('Soportes')}
              </div>
              <div className="flex items-center gap-1">
                {item.supports.length}/{sessionSupports.length}
                <ChevronDownIcon size={IconSizeEnum.SM} />
              </div>
            </div>
          </button>
        </div>
      </div>

      <div data-id="card-widget-content" className="min-h-0 flex-1 overflow-y-auto">
        <TimeKeepingAccordion
          compositeId={compositeId}
          supports={item.supports}
          buttonStates={buttonStates}
          openAccordions={openAccordions}
          allSupports={allSupports}
          playerVideoIsPlaying={playerVideoIsPlaying}
          playerVideoCurrentTime={playerVideoCurrentTime}
          getDateTimeCurrentPresence={getDateTimeCurrentPresence}
          liRefs={liRefs}
          onToggleAccordion={toggleAccordion}
          onPlayPause={handlePlayPause}
          onResetSupport={requestResetSupport}
          onRemoveRecord={onRemoveRecord}
          onToggleVisibility={onOpenVisibility}
        />
      </div>

      {openSelector &&
        selectorCoords &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[99]" onPointerDown={() => setOpenSelector(false)} />
            <div
              className="fixed z-[100] min-w-[200px] border border-neutral-20 bg-white p-3 shadow-lg"
              style={{ top: selectorCoords.top, left: selectorCoords.left }}
            >
              <Listbox
                dark={false}
                mode="multiple"
                items={filteredSupportOptions}
                onChange={next => onItemSupportsChange(compositeId, next)}
                identifiers={{ key: 'value', value: 'value', label: 'label' }}
                selected={item.supports.map(s => s.presenceId)}
                onClose={() => setOpenSelector(false)}
              />
            </div>
          </>,
          document.body
        )}

      <TimeKeepingResetModal
        isOpen={pendingReset !== null}
        scope={pendingReset?.presenceId ? 'support' : 'all'}
        onClose={cancelReset}
        onConfirm={handleConfirmReset}
      />
    </div>
  );
}
